// @flow
import { AdSlot, GraphController, Grid, GridItem, MobileGraphController, Query, SeoData, useMedia, } from '@haaretz/htz-components';
import React, { Fragment, } from 'react';
import { FelaTheme, } from 'react-fela';

import type { Node, } from 'react';

import AssetStaticGraphs from '../../components/AssetStaticGraphs/AssetStaticGraphs';
import PageRow from '../../components/PageRow/PageRow';
import QuoteInfoTable from '../../components/QuotePageComponents/QuoteInfoTable/QuoteInfoTable';
import QuoteSummary from '../../components/QuotePageComponents/QuoteSummary/QuoteSummary';
import RowItem from '../../components/RowItem/RowItem';
import MainLayout from '../../layouts/MainLayout';
import ExchangeQuery from '../queries/exchangeData.graphql';
import useAllAdUnits from '../../components/AdManager/AdUnitsProvider';
import useAdUnitPathTemplate, { createAdSlotProps, } from '../../components/AdManager/useAdUnitPathTemplate';

type Props = {
  url: {
    pathname: string,
    query: {
      assetId: string,
      section: string,
    },
    asPath: string,
  },
  responseEnd: () => void,
  responseWriteHead: (statusCode: number, headers: { [key: string]: string | number | boolean, }) => void,
};

function exchange({
  url: {
    query: { assetId, section, },
    asPath,
  },
  responseEnd,
  responseWriteHead,
}: Props): Node {
  const crypto: boolean = section === 'crypto';
  const isMobile = useMedia({ query: { from: 's', }, });
  const adUnits = useAllAdUnits();
  const adUnitPathTemplate = useAdUnitPathTemplate();

  return (
    <MainLayout section={section}>
      <Query partialRefetch query={ExchangeQuery} variables={{ assetId, }}>
        {({ loading, error, data, }) => {
          if (error) return null;
          if (loading) return null;
          if (!data.assets?.length) return null;

          const type = data.assets[0]?.type?.toLowerCase();

          if (type && section !== type) {
            responseWriteHead(301, { Location: encodeURI(asPath.replace(section, type)), });
            responseEnd();

            return null;
          }

          const {
            name,
            value,
            changePercentage,
            fixedRate,
            subType,
            lastTradeTime,
            yearlyYield,
            ...staticGraphsData
          } = data.assets[0];
          return (
            <FelaTheme>
              {theme => (
                <Fragment>
                  <SeoData
                    title={
                      crypto
                        ? `${name} - שער ${name} - מטבעות דיגיטלים - TheMarker Finance`
                        : `${name} -מט"ח – נתוני מסחר מטבע חוץ TheMarker Finance`
                    }
                    description={
                      crypto
                        ? `${name} שערים עדכנים והיסטוריים של מטבע ${name} - כל המידע על מטבעות דיגיטליים ומטבעות קריפטוגרפים באתר TheMarker Finance`
                        : `${name} למידע על נתוני מסחר של מטבעות חוץ, שערי מט"ח, אופציות מט"ח, שערים יציגים ועוד , היכנסו לאתר TheMarker Finance`
                    }
                    path={asPath}
                  />
                  <PageRow lines={2}>
                    <RowItem
                      title={name}
                      miscStyles={{
                        ...theme.type(5),
                      }}
                    />
                  </PageRow>
                  <PageRow
                    miscStyles={{
                      marginBottom: '2rem',
                    }}
                  >
                    <QuoteSummary
                      valueData={[
                        { title: 'שער', value: value ? value.toString() : '', decimal: 4, },
                        { title: '% שינוי', value: changePercentage, },
                        fixedRate
                          ? { title: 'שער יציג', value: fixedRate, decimal: 4, }
                          : { title: 'תשואה שנתית', value: yearlyYield, percentage: true, },
                      ]}
                      date={{ title: 'נכון ל:', value: lastTradeTime, }}
                      assetInfo={[ { title: 'סוג נייר:', value: subType, }, ]}
                    />
                  </PageRow>
                  <PageRow>
                    {
                      isMobile
                        ? <GraphController assetId={assetId} width={900} />
                        : <MobileGraphController assetId={assetId} />
                    }
                  </PageRow>
                  <PageRow>
                    <Grid
                      gutter={2}
                      miscStyles={{
                        paddingStart: '0rem',
                        paddingEnd: '0rem',
                      }}
                    >
                      <QuoteInfoTable
                        id={assetId}
                        width={[
                          { until: 'l', value: 1, },
                          { from: 'l', value: 1 / 3, },
                        ]}
                        title="נתוני המסחר"
                        tradingStatus
                        fixed
                        fields={[
                          ...(!crypto ? [ { name: 'baseValue', display: 'שער בסיס', }, ] : []),
                          ...(crypto ? [ { name: 'openingValue', display: 'שער פתיחה', }, ] : []),
                          { name: 'value', display: 'שער אחרון', },
                          { name: 'dailyLow', display: 'נמוך יומי', },
                          { name: 'dailyHigh', display: 'גבוה יומי', },
                          ...(!crypto ? [
                            { name: 'yearLow', display: 'נמוך שנתי', },
                            { name: 'yearHigh', display: 'גבוה שנתי', },
                          ] : []),
                          ...(crypto ? [ { name: 'marketCap', display: 'שווי שוק', }, ] : []),
                        ]}
                      />
                      <GridItem
                        width={[
                          { until: 'l', value: 1, },
                          { from: 'l', value: 2 / 3, },
                        ]}
                      >
                        <AssetStaticGraphs {...staticGraphsData} yearlyYield={yearlyYield} />
                      </GridItem>
                    </Grid>
                  </PageRow>
                  <PageRow>
                    <RowItem>
                      <AdSlot {...createAdSlotProps(adUnits['finance.themarker.com.banner1'], adUnitPathTemplate)} />
                    </RowItem>
                  </PageRow>
                </Fragment>
              )}
            </FelaTheme>
          );
        }}
      </Query>
    </MainLayout>
  );
}

export default exchange;
